import { apiPost } from './api';

/**
 * #715 — the bell's link to the thread readers.
 *
 * Every inbound email is its own bell entry, so the bell would become a
 * second inbox unless opening the mail cleared it. The server clears entries
 * on any mail unread→read transition (the ROW rule); this is the OPENER rule
 * for the reader that opens a thread without changing mail state — the
 * unified reader never marks shared mail read on open.
 */

/** Window event TopBar listens for to refetch the badge (and open list). */
export const NOTIFICATIONS_CHANGED_EVENT = 'ncrm:notifications-changed';

/** Matches the server's `comm_ids` max_length (and MAIL_TARGET_ID_CAP). */
export const READ_BY_MAIL_CAP = 200;

/** Tell TopBar the bell may have changed. */
export function notifyBellChanged() {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

/**
 * The caller opened these messages: mark THEIR bell entries for them read.
 * Fire-and-forget — the bell must never break a reader. Never marks mail read.
 *
 * `headers` is the auth-header object or the `getAuthHeader` getter itself;
 * a getter is resolved here, inside the guard, so even a throwing getter
 * cannot take the reader's effect down with it.
 */
export function markMailNotificationsRead(commIds, headers) {
    try {
        const ids = [...new Set((commIds || []).filter(
            id => id && !String(id).startsWith('synthetic-'),
        ))];
        if (!ids.length) return Promise.resolve();
        const resolved = typeof headers === 'function' ? headers() : headers;
        return apiPost('/notifications/read-by-mail', { comm_ids: ids.slice(0, READ_BY_MAIL_CAP) }, resolved)
            .then(notifyBellChanged)
            .catch(() => {}); // never break the reader over the bell
    } catch {
        return Promise.resolve();
    }
}

/**
 * #715 — the message card the bell asked a reader to open at, or null.
 * Matched by iterating rather than by a `[data-comm-id="…"]` selector, so an
 * id with selector metacharacters can never throw or match the wrong card.
 */
export function findFocusedMessage(container, focusCommId) {
    if (!container || !focusCommId) return null;
    const cards = container.querySelectorAll('[data-comm-id]');
    for (const card of cards) {
        if (card.getAttribute('data-comm-id') === String(focusCommId)) return card;
    }
    return null;
}

/**
 * #715 audit F05 — a focus hand-off is consumed ONCE per history entry.
 *
 * `focusCommId` rides in router state, which the browser keeps on the history
 * entry — so a refresh, or Back to the thread, would re-apply it. Clearing it
 * with a `replace` navigation would change `location.state`'s identity, which
 * both readers' memos read (`state.email`, `threadNav`). Instead the readers
 * record `${location.key}:${focusCommId}` here once they apply it; a later
 * mount of the same entry sees it consumed and behaves as if there were no
 * hand-off. sessionStorage because history entries are per tab. Every access
 * is guarded: storage can be absent or throw (private mode, blocked site data).
 */
const FOCUS_CONSUMED_STORE = 'ncrm:bell-focus-consumed';
const FOCUS_CONSUMED_MAX = 25;

/** The history-entry key for a hand-off, or null when there is nothing to key. */
export function focusKeyFor(locationKey, focusCommId) {
    // 'default' is the initial entry's shared key — not unique, never persisted.
    if (!focusCommId || !locationKey || locationKey === 'default') return null;
    return `${locationKey}:${focusCommId}`;
}

function readConsumed() {
    try {
        const raw = window.sessionStorage.getItem(FOCUS_CONSUMED_STORE);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

export function isFocusConsumed(key) {
    return Boolean(key) && readConsumed().includes(key);
}

export function markFocusConsumed(key) {
    if (!key) return;
    try {
        const list = readConsumed().filter(k => k !== key);
        list.push(key);
        window.sessionStorage.setItem(
            FOCUS_CONSUMED_STORE, JSON.stringify(list.slice(-FOCUS_CONSUMED_MAX)));
    } catch {
        // Storage unavailable: the focus may re-apply on refresh. Harmless.
    }
}

/**
 * #715 audit F04 — keep a focused card where it was scrolled to while the
 * content above it settles.
 *
 * Email bodies render in iframes that auto-resize after load (and then poll
 * for a few seconds), so the cards ABOVE the focused one grow after the
 * one-off `scrollIntoView` and push it down, possibly off screen. Native scroll
 * anchoring covers this in some browsers; this covers the rest. For
 * `durationMs` it re-measures the card's offset inside the scroll container
 * and scrolls by any drift. It stops early the moment the user acts —
 * wheel, touch, pointer or a key — so it never fights a deliberate scroll.
 * Returns a stop function.
 */
export const FOCUS_HOLD_MS = 6000;
const FOCUS_HOLD_TICK_MS = 200;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown'];

export function holdFocusedMessage(container, card, durationMs = FOCUS_HOLD_MS) {
    if (!container || !card) return () => {};
    const offset = () => card.getBoundingClientRect().top - container.getBoundingClientRect().top;
    const target = offset();
    let stopped = false;
    let tick = null;
    let deadline = null;
    const stop = () => {
        if (stopped) return;
        stopped = true;
        clearInterval(tick);
        clearTimeout(deadline);
        USER_SCROLL_EVENTS.forEach(e => container.removeEventListener(e, stop));
        window.removeEventListener('keydown', stop);
    };
    USER_SCROLL_EVENTS.forEach(e => container.addEventListener(e, stop, { passive: true }));
    window.addEventListener('keydown', stop);
    tick = setInterval(() => {
        const drift = offset() - target;
        if (Math.abs(drift) > 1) container.scrollTop += drift;
    }, FOCUS_HOLD_TICK_MS);
    deadline = setTimeout(stop, durationMs);
    return stop;
}
